import React, { useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Dialog } from '@headlessui/react';
import { QrCodeIcon, ArrowDownTrayIcon, ShareIcon } from '@heroicons/react/24/outline';
import { Button } from './common/Button';
import { Card, CardContent } from './common/Card';

interface QRCodeGeneratorProps {
  userId?: number | string;
  username?: string;
}

const QRCodeGenerator: React.FC<QRCodeGeneratorProps> = ({
  userId,
  username
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);

  // Generate QR code data with app URL and API URL
  const generateQRData = () => {
    const data = {
      // Use the current hostname for the app URL when deployed
      app: window.location.origin,
      // Use relative API URL for production or the provided one
      api: window.location.origin + '/api/v1',
      timestamp: new Date().toISOString(),
      ...(userId && { userId }),
      ...(username && { username })
    };
    
    return JSON.stringify(data);
  };

  const qrValue = generateQRData();

  // Download QR code as image
  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-code-canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const pngUrl = canvas
      .toDataURL('image/png')
      .replace('image/png', 'image/octet-stream');
    
    const downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = `leave-app-qr-${new Date().getTime()}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    
    setShowSuccessMsg(true);
    setTimeout(() => setShowSuccessMsg(false), 3000);
  };

  // Share QR code (for mobile devices)
  const shareQRCode = async () => {
    try {
      const canvas = document.getElementById('qr-code-canvas') as HTMLCanvasElement;
      if (!canvas) return;
      
      const blob = await new Promise<Blob>((resolve) => 
        canvas.toBlob((blob) => resolve(blob!), 'image/png')
      );
      
      const file = new File([blob], 'leave-app-qr.png', { type: 'image/png' });
      
      if (navigator.share) {
        await navigator.share({
          title: 'Leave App Mobile Pairing QR Code',
          text: 'Scan this QR code with your Leave App mobile application to connect.',
          files: [file]
        });
        setShowSuccessMsg(true);
        setTimeout(() => setShowSuccessMsg(false), 3000);
      } else {
        downloadQRCode();
      }
    } catch (error) {
      console.error('Error sharing QR code:', error);
    }
  };
  
  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        className="flex items-center"
        variant="primary"
      >
        <QrCodeIcon className="h-5 w-5 mr-2" />
        Generate Mobile Pairing QR
      </Button>
      
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-sm rounded bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
              Mobile App Pairing QR Code
            </Dialog.Title>
            
            <div className="bg-white p-3 rounded-md flex justify-center items-center">
              <QRCodeCanvas
                id="qr-code-canvas"
                value={qrValue}
                size={220}
                level="H"
                includeMargin
              />
            </div>
            
            <p className="mt-4 text-sm text-gray-500 text-center mb-4">
              Scan this QR code with your Leave App mobile application to connect it to this account.
            </p>
            
            {showSuccessMsg && (
              <div className="mb-4 p-2 bg-green-50 text-green-700 text-sm rounded">
                QR code downloaded successfully!
              </div>
            )}
            
            <div className="mt-4 flex space-x-3">
              <Button
                onClick={downloadQRCode}
                className="flex-1 flex justify-center items-center"
                variant="secondary"
              >
                <ArrowDownTrayIcon className="h-5 w-5 mr-1" />
                Download
              </Button>
              
              <Button
                onClick={shareQRCode}
                className="flex-1 flex justify-center items-center"
                variant="primary"
              >
                <ShareIcon className="h-5 w-5 mr-1" />
                Share
              </Button>
            </div>
            
            <div className="mt-4">
              <Button
                onClick={() => setIsOpen(false)}
                className="w-full"
                variant="outline"
              >
                Close
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
};

export default QRCodeGenerator;